import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Icon } from '../../components/ui/Icon';
import { Modal } from '../../components/ui/Modal';
import { StatusDot } from '../../components/ui/StatusDot';
import type { Medidor } from '../../../domain/entities/Medidor';
import type { NumeroRele } from '../../../data/repositories/RepositorioMedidores';
import { formatearFechaHora } from '../../utils/formato';
import estilos from './Energia.module.css';

interface DetalleMedidorProps {
  medidor: Medidor | null;
  onCerrar: () => void;
  puedeAccionar: boolean;
  motivoSinPermiso: string | null;
  onAccionarRele: (rele: NumeroRele, encender: boolean) => void;
  releEnCurso: NumeroRele | null;
}

function formatear(valor: number | null, decimales = 2): string {
  return valor === null ? '—' : valor.toFixed(decimales);
}

function Magnitud({
  etiqueta,
  valor,
  unidad,
}: {
  etiqueta: string;
  valor: string;
  unidad?: string;
}) {
  return (
    <div className={estilos.magnitud}>
      <div className={estilos.magnitudEtiqueta}>{etiqueta}</div>
      <div className={estilos.magnitudValor}>
        {valor}
        {unidad && <span className={estilos.magnitudUnidad}>{unidad}</span>}
      </div>
    </div>
  );
}

/** Ficha completa de un medidor con todas sus variables y el control de relés. */
export function DetalleMedidor({
  medidor,
  onCerrar,
  puedeAccionar,
  motivoSinPermiso,
  onAccionarRele,
  releEnCurso,
}: DetalleMedidorProps) {
  if (!medidor) return null;

  const tensiones = medidor.tensionesPorFase;
  const corrientes = medidor.corrientesPorFase;
  const factores = medidor.factorPotenciaPorFase;

  return (
    <Modal
      abierto
      onCerrar={onCerrar}
      ancho
      titulo={medidor.nombre}
      descripcion={`Serial ${medidor.serial} · ${medidor.protocolo}`}
      pie={
        <Button variante="secundario" onClick={onCerrar}>
          Cerrar
        </Button>
      }
    >
      <div className={estilos.detalle}>
        {medidor.lecturaCorrecta === false && medidor.mensajeError && (
          <div className={estilos.error}>
            <Icon nombre="errorContorno" tamano={16} />
            {medidor.mensajeError}
          </div>
        )}

        <section className={estilos.bloque}>
          <h3 className={estilos.bloqueTitulo}>Resumen</h3>
          <div className={estilos.variables}>
            <Magnitud etiqueta="Suministro" valor={medidor.tipoSuministroEtiqueta} />
            <Magnitud
              etiqueta="Tensión"
              valor={formatear(medidor.tensionMostrable, 1)}
              unidad="V"
            />
            <Magnitud etiqueta="Potencia" valor={formatear(medidor.potenciaTotal, 0)} unidad="W" />
            <Magnitud etiqueta="Frecuencia" valor={formatear(medidor.frecuencia, 2)} unidad="Hz" />
            <Magnitud
              etiqueta="Energía consumida"
              valor={formatear(medidor.energiaTotal, 2)}
              unidad="kWh"
            />
            <Magnitud
              etiqueta="Energía entregada"
              valor={formatear(medidor.energiaInversa, 2)}
              unidad="kWh"
            />
          </div>
        </section>

        <section className={estilos.bloque}>
          <h3 className={estilos.bloqueTitulo}>Por fase</h3>
          <div className={estilos.variables}>
            {[0, 1, 2].map((i) => (
              <Magnitud
                key={`v${i}`}
                etiqueta={`Tensión L${i + 1}`}
                valor={formatear(tensiones[i] ?? null, 1)}
                unidad="V"
              />
            ))}
            {[0, 1, 2].map((i) => (
              <Magnitud
                key={`a${i}`}
                etiqueta={`Corriente L${i + 1}`}
                valor={formatear(corrientes[i] ?? null, 2)}
                unidad="A"
              />
            ))}
            {[0, 1, 2].map((i) => (
              <Magnitud
                key={`fp${i}`}
                etiqueta={`Factor de potencia L${i + 1}`}
                valor={formatear(factores[i] ?? null, 2)}
              />
            ))}
          </div>
        </section>

        <section className={estilos.bloque}>
          <h3 className={estilos.bloqueTitulo}>Corte remoto de energía</h3>
          <p className={estilos.explicacion}>
            Este medidor lleva interruptores internos que permiten cortar o restablecer el paso de
            la energía a distancia. Cortar deja sin electricidad a todo lo que dependa de esa línea.
          </p>

          <div className={estilos.reles}>
            {([1, 2] as NumeroRele[]).map((numero) => {
              const conPaso = medidor.estadoRele(numero);
              const ocupado = releEnCurso === numero;

              return (
                <div key={numero} className={estilos.rele}>
                  <StatusDot
                    color={
                      conPaso === null
                        ? 'var(--text-3)'
                        : conPaso
                          ? 'var(--status-success)'
                          : 'var(--status-danger)'
                    }
                    encendido={conPaso !== null}
                    texto={
                      conPaso === null
                        ? `Línea ${numero}: sin información`
                        : `Línea ${numero}: ${conPaso ? 'CON ENERGÍA' : 'CORTADA'}`
                    }
                    tamano={10}
                  />

                  <div className={estilos.releAcciones}>
                    <Button
                      tamano="sm"
                      variante="exito"
                      icono="encender"
                      cargando={ocupado}
                      motivoDeshabilitado={puedeAccionar ? null : motivoSinPermiso}
                      onClick={() => onAccionarRele(numero, true)}
                    >
                      Dar energía
                    </Button>
                    <Button
                      tamano="sm"
                      variante="peligro"
                      icono="apagar"
                      cargando={ocupado}
                      motivoDeshabilitado={puedeAccionar ? null : motivoSinPermiso}
                      onClick={() => {
                        // Dejar sin luz una instalación no puede depender de un
                        // clic accidental.
                        const seguro = window.confirm(
                          `¿Cortar la energía de la línea ${numero} en «${medidor.nombre}»?\n\n` +
                            'Todo lo conectado a esa línea se quedará sin electricidad hasta que ' +
                            'vuelvas a darle paso desde aquí.',
                        );
                        if (seguro) onAccionarRele(numero, false);
                      }}
                    >
                      Cortar energía
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className={estilos.bloque}>
          <h3 className={estilos.bloqueTitulo}>Equipo</h3>
          <div className={estilos.variables}>
            <Magnitud etiqueta="Cliente" valor={medidor.clienteNombre ?? '—'} />
            <Magnitud etiqueta="Origen" valor={medidor.origen ?? '—'} />
            <Magnitud etiqueta="Pasarela" valor={medidor.gateway ?? '—'} />
            <Magnitud
              etiqueta="Sondeo"
              valor={
                medidor.sondeoActivo
                  ? `Cada ${medidor.intervaloSondeoSegundos ?? '—'} s`
                  : 'Desactivado'
              }
            />
            <Magnitud etiqueta="Última lectura" valor={formatearFechaHora(medidor.ultimaLectura)} />
          </div>
        </section>

        {!medidor.tieneLecturas && (
          <Badge color="var(--text-2)" grande>
            Este medidor todavía no ha entregado lecturas
          </Badge>
        )}
      </div>
    </Modal>
  );
}
